import { Model } from 'jms-domain'
import type { $Wall } from 'jms-domain/src/__generated/enhanced-model'
import { match } from 'ts-pattern'

import { configuratorInterface } from '../systems/configurator-interface'

/**
 * Assign the first available position to a newly added CountertopWallItem.
 * Searches through continuous countertop spaces and tries multiple walls based on room shape.
 */
export const assignCountertopItemPositionTransformer = configuratorInterface.transformerBuilder.when
  .entity(Model.CountertopWallItem.types)
  .created()
  .then.applySideEffect(ctx => {
    const createdEntity = ctx.getNew$Entity()
    const $root = ctx.getOld$Root()

    /**
     * Helper function to find an available position on a given wall
     */
    const findPositionOnWall = ($wall: $Wall): { x: number; wallId: Model.Wall['id'] } | null => {
      // Get continuous countertop spaces
      const countertopSpaces = $wall.$continuousCountertopSpaces

      if (countertopSpaces.length === 0) {
        return null
      }

      // Get existing countertop items on this wall
      const existingItems = $wall.$countertopItems.filter(item => item.id !== createdEntity.id)

      const itemWidth = createdEntity.$width

      // Try each countertop space
      for (const space of countertopSpaces) {
        // Check if we can place the item within the space
        // The item's x position is its leftmost point, so we need to ensure:
        // x >= space.minX and x + width <= space.maxX
        const minX = space.minX
        const maxX = space.maxX - itemWidth

        if (minX > maxX) {
          // Space is too small for this item
          continue
        }

        // Try to find a position in this space that doesn't conflict with existing items
        let currentX = minX

        while (currentX <= maxX) {
          const itemStart = currentX
          const itemEnd = currentX + itemWidth

          // Check if this position conflicts with any existing items
          const hasConflict = existingItems.some(existingItem => {
            const existingStart = existingItem.x
            const existingEnd = existingItem.x + existingItem.$width

            // Check for overlap
            return !(itemEnd <= existingStart || itemStart >= existingEnd)
          })

          if (!hasConflict) {
            // Found a valid position!
            return {
              x: currentX,
              wallId: $wall.id,
            }
          }

          // Move to the next potential position (try after the conflicting item)
          const conflictingItem = existingItems.find(existingItem => {
            const existingStart = existingItem.x
            const existingEnd = existingItem.x + existingItem.$width
            return !(itemEnd <= existingStart || itemStart >= existingEnd)
          })

          if (conflictingItem) {
            // Move past the conflicting item
            currentX = conflictingItem.x + conflictingItem.$width
          } else {
            break
          }
        }
      }

      return null
    }

    // Try walls based on room shape, similar to AssignWallItemToZoneTransformerComponent
    return match($root.shape)
      .with('I', () => {
        // For I-shape, only wallB is active
        const position = findPositionOnWall($root.$wallB)

        if (!position) {
          return []
        }

        return [
          configuratorInterface.updates.DomainEntityModification({
            entityId: createdEntity.id,
            stateDelta: {
              x: position.x,
              wallId: position.wallId,
            },
            updateTag: 'assignCountertopItemPosition',
          }),
        ]
      })
      .with('L', () => {
        // For L-shape, try wallA first, then wallB
        const positionA = findPositionOnWall($root.$wallA)

        if (positionA) {
          return [
            configuratorInterface.updates.DomainEntityModification({
              entityId: createdEntity.id,
              stateDelta: {
                x: positionA.x,
                wallId: positionA.wallId,
              },
              updateTag: 'assignCountertopItemPosition',
            }),
          ]
        }

        const positionB = findPositionOnWall($root.$wallB)

        if (!positionB) {
          return []
        }

        return [
          configuratorInterface.updates.DomainEntityModification({
            entityId: createdEntity.id,
            stateDelta: {
              x: positionB.x,
              wallId: positionB.wallId,
            },
            updateTag: 'assignCountertopItemPosition',
          }),
        ]
      })
      .with('U', () => {
        // For U-shape, try wallA first, then wallB, then wallC
        const positionA = findPositionOnWall($root.$wallA)

        if (positionA) {
          return [
            configuratorInterface.updates.DomainEntityModification({
              entityId: createdEntity.id,
              stateDelta: {
                x: positionA.x,
                wallId: positionA.wallId,
              },
              updateTag: 'assignCountertopItemPosition',
            }),
          ]
        }

        const positionB = findPositionOnWall($root.$wallB)

        if (positionB) {
          return [
            configuratorInterface.updates.DomainEntityModification({
              entityId: createdEntity.id,
              stateDelta: {
                x: positionB.x,
                wallId: positionB.wallId,
              },
              updateTag: 'assignCountertopItemPosition',
            }),
          ]
        }

        const positionC = findPositionOnWall($root.$wallC)

        if (!positionC) {
          return []
        }

        return [
          configuratorInterface.updates.DomainEntityModification({
            entityId: createdEntity.id,
            stateDelta: {
              x: positionC.x,
              wallId: positionC.wallId,
            },
            updateTag: 'assignCountertopItemPosition',
          }),
        ]
      })
      .exhaustive()
  })
  .build('Assign countertop item position')
